import { useSessionStore } from '../store/sessionStore';
import { getAgentInitials } from '../utils/agentColors';
import { getRelativeTime } from '../utils/time';
import './AgentTabs.css';

export function AgentTabs() {
  const { nodes, selectedNodeId, selectNode, events, anomalies } = useSessionStore();

  const agentList = Object.values(nodes);

  function getAgentStats(id: string) {
    const agentEvents = events.filter(
      (e) => e.agent_id === id || e.source_agent === id || e.target_agent === id
    );
    const sent = agentEvents.filter((e) => e.source_agent === id);
    const received = agentEvents.filter((e) => e.target_agent === id || e.agent_id === id);
    const errors = agentEvents.filter((e) => e.event_type === 'error' && e.agent_id === id);
    const tools = [...new Set(agentEvents.filter((e) => e.tool_calls.length > 0).flatMap((e) => e.tool_calls))];
    const totalTokens = agentEvents.reduce((sum, e) => sum + e.token_count, 0);
    return { sent: sent.length, received: received.length, errors: errors.length, tools, totalTokens };
  }

  if (agentList.length === 0) {
    return (
      <div className="agent-tabs-empty">
        <p>No agents detected yet</p>
      </div>
    );
  }

  if (selectedNodeId && nodes[selectedNodeId]) {
    const node = nodes[selectedNodeId];
    const stats = getAgentStats(selectedNodeId);
    const agentAnomalies = anomalies.filter((a) => a.involved_agents.includes(selectedNodeId));

    return (
      <div className="agent-detail">
        <button className="agent-detail-back" onClick={() => selectNode(null)}>
          ← All Agents
        </button>
        <div className="agent-detail-card">
          <div className="agent-detail-avatar" style={{ background: node.color }}>
            {getAgentInitials(node.id)}
          </div>
          <div className="agent-detail-info">
            <h3 className="agent-detail-name mono">{node.id}</h3>
            <div className="agent-detail-stats">
              <span>{stats.sent} sent</span>
              <span className="dot">·</span>
              <span>{stats.received} recv</span>
              <span className="dot">·</span>
              <span>{stats.totalTokens.toLocaleString()} tokens</span>
            </div>
            <div className="agent-detail-time">
              Last active: {getRelativeTime(node.lastActive)}
            </div>
          </div>
        </div>

        {stats.errors > 0 && (
          <div className="agent-detail-section">
            <h4 className="agent-detail-section-title text-red">Errors ({stats.errors})</h4>
            <div className="agent-detail-errors">
              {events
                .filter((e) => e.event_type === 'error' && e.agent_id === selectedNodeId)
                .map((e) => (
                  <div key={e.event_id} className="agent-error-entry">
                    <span className="text-dim">{new Date(e.timestamp_ms).toLocaleTimeString()}</span>
                    <span>{e.error}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {stats.tools.length > 0 && (
          <div className="agent-detail-section">
            <h4 className="agent-detail-section-title">Tool Calls</h4>
            <div className="agent-detail-tools">
              {stats.tools.map((t) => (
                <span key={t} className="tool-chip">{t}</span>
              ))}
            </div>
          </div>
        )}

        {agentAnomalies.length > 0 && (
          <div className="agent-detail-section">
            <h4 className="agent-detail-section-title text-amber">Anomalies</h4>
            {agentAnomalies.map((a) => (
              <div key={a.anomaly_id} className="agent-anomaly-entry">
                <span className={`anomaly-badge ${a.severity}`}>{a.anomaly_type}</span>
                <span>{a.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="agent-grid">
      {agentList.map((node) => {
        const _unused = getAgentStats(node.id); void _unused;
        const hasAnomaly = anomalies.some((a) => a.involved_agents.includes(node.id) && !a.acknowledged);
        return (
          <div
            key={node.id}
            className={`agent-card ${hasAnomaly ? 'agent-card--anomaly' : ''}`}
            onClick={() => selectNode(node.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && selectNode(node.id)}
          >
            <div className="agent-card-avatar" style={{ background: node.color }}>
              {getAgentInitials(node.id)}
            </div>
            <div className="agent-card-info">
              <span className="agent-card-name">{node.id}</span>
              <span className="agent-card-msgs">{node.messageCount} msgs</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
